import React from 'react';
import { Sun, Moon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from '../ThemeContext';

export interface ThemeToggleProps {
  /**
   * 'icon': Icon button for headers/toolbars (default)
   * 'pill': Compact pill button with icon + text
   * 'menu-item': Full width row for dropdowns (e.g. UserMenu)
   */
  variant?: 'icon' | 'pill' | 'menu-item';
  className?: string;
  size?: 'sm' | 'md' | 'lg';
  showLabel?: boolean;
}

export const ThemeToggle: React.FC<ThemeToggleProps> = ({
  variant = 'icon',
  className = '',
  size = 'md',
  showLabel = false,
}) => {
  const { isDarkMode, toggleTheme } = useTheme();

  const iconSizes = {
    sm: 14,
    md: 16,
    lg: 18,
  };
  const iconSize = iconSizes[size] || 16;

  // 1. Menu Item variant (for UserMenu dropdowns)
  if (variant === 'menu-item') {
    return (
      <button
        type="button"
        onClick={(e) => {
          e?.stopPropagation?.();
          toggleTheme();
        }}
        className={`w-full flex items-center justify-between px-3 py-2 text-sm rounded-lg transition-all duration-200 group ${
          isDarkMode
            ? 'text-slate-100 hover:bg-slate-700/60'
            : 'text-slate-800 hover:bg-slate-100'
        } ${className}`}
        role="switch"
        aria-checked={isDarkMode}
        aria-label="Toggle dark mode"
      >
        <div className="flex items-center gap-3">
          <div
            className={`w-7 h-7 rounded-lg flex items-center justify-center transition-colors ${
              isDarkMode
                ? 'bg-amber-400/10 text-amber-300'
                : 'bg-blue-50 text-[#0084CF]'
            }`}
          >
            {isDarkMode ? <Sun size={15} /> : <Moon size={15} />}
          </div>
          <div className="text-left">
            <span className="block font-medium text-xs sm:text-sm">
              {isDarkMode ? 'Dark Theme' : 'Light Theme'}
            </span>
            <span
              className={`block text-[11px] ${
                isDarkMode ? 'text-slate-400' : 'text-slate-500'
              }`}
            >
              {isDarkMode ? 'Click to switch to Light' : 'Click to switch to Dark'}
            </span>
          </div>
        </div>

        {/* Apple/Tailwind style pill switch */}
        <div
          className={`w-9 h-5 flex items-center rounded-full p-0.5 transition-colors duration-300 ${
            isDarkMode ? 'bg-[#0084CF]' : 'bg-slate-300'
          }`}
        >
          <motion.div
            layout
            transition={{ type: 'spring', stiffness: 500, damping: 30 }}
            className={`w-4 h-4 bg-white rounded-full shadow-md transform ${
              isDarkMode ? 'translate-x-4' : 'translate-x-0'
            }`}
          />
        </div>
      </button>
    );
  }

  // 2. Pill variant (Icon + Text)
  if (variant === 'pill') {
    return (
      <button
        type="button"
        onClick={(e) => {
          e?.stopPropagation?.();
          toggleTheme();
        }}
        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold transition-all duration-200 border shadow-sm ${
          isDarkMode
            ? 'bg-slate-800/90 text-amber-300 border-slate-700 hover:bg-slate-700/90 hover:border-slate-600'
            : 'bg-white/95 text-slate-700 border-slate-200 hover:bg-slate-50 hover:text-slate-900'
        } ${className}`}
        title={isDarkMode ? 'Switch to Light Theme' : 'Switch to Dark Theme'}
        aria-label={isDarkMode ? 'Switch to Light Theme' : 'Switch to Dark Theme'}
      >
        <AnimatePresence mode="wait" initial={false}>
          <motion.span
            key={isDarkMode ? 'dark' : 'light'}
            initial={{ y: -8, opacity: 0, rotate: -45 }}
            animate={{ y: 0, opacity: 1, rotate: 0 }}
            exit={{ y: 8, opacity: 0, rotate: 45 }}
            transition={{ duration: 0.18 }}
            className="flex items-center"
          >
            {isDarkMode ? (
              <Sun size={iconSize} className="text-amber-400" />
            ) : (
              <Moon size={iconSize} className="text-[#0084CF]" />
            )}
          </motion.span>
        </AnimatePresence>
        <span>{isDarkMode ? 'Dark' : 'Light'}</span>
      </button>
    );
  }

  // 3. Icon button variant (Header/Toolbar)
  const buttonDimensions = {
    sm: 'w-7 h-7',
    md: 'w-8 h-8 sm:w-9 sm:h-9',
    lg: 'w-10 h-10',
  };

  return (
    <motion.button
      type="button"
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.92 }}
      onClick={(e) => {
        e?.stopPropagation?.();
        toggleTheme();
      }}
      className={`relative inline-flex items-center justify-center rounded-xl transition-all duration-200 border backdrop-blur-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[#0084CF]/40 ${
        buttonDimensions[size] || 'w-8 h-8 sm:w-9 sm:h-9'
      } ${
        isDarkMode
          ? 'bg-slate-800/80 text-amber-300 border-slate-700/80 hover:bg-slate-700 hover:text-amber-200 hover:border-slate-600 shadow-slate-950/40'
          : 'bg-white/80 text-slate-700 border-slate-200/90 hover:bg-white hover:text-[#0084CF] hover:border-slate-300 shadow-slate-200/50'
      } ${className}`}
      title={isDarkMode ? 'Switch to Light Theme' : 'Switch to Dark Theme'}
      aria-label={isDarkMode ? 'Switch to Light Theme' : 'Switch to Dark Theme'}
    >
      <AnimatePresence mode="wait" initial={false}>
        <motion.span
          key={isDarkMode ? 'sun' : 'moon'}
          initial={{ rotate: -90, scale: 0.5, opacity: 0 }}
          animate={{ rotate: 0, scale: 1, opacity: 1 }}
          exit={{ rotate: 90, scale: 0.5, opacity: 0 }}
          transition={{ duration: 0.2, ease: 'easeOut' }}
          className="flex items-center justify-center"
        >
          {isDarkMode ? (
            <Sun size={iconSize} className="text-amber-400 drop-shadow-[0_0_6px_rgba(251,191,36,0.5)]" />
          ) : (
            <Moon size={iconSize} className="text-slate-700 group-hover:text-[#0084CF]" />
          )}
        </motion.span>
      </AnimatePresence>

      {showLabel && (
        <span className="ml-1.5 text-xs font-semibold">
          {isDarkMode ? 'Dark' : 'Light'}
        </span>
      )}
    </motion.button>
  );
};

export default ThemeToggle;
